// src/components/Card.tsx

import { FC, ReactNode } from "react";
import clsx from "clsx";

interface CardProps {
  children: ReactNode;
  className?: string;
  header?: ReactNode;
  footer?: ReactNode;
  variant?: "default" | "elevated" | "outlined" | "ghost";
  padding?: "sm" | "md" | "lg";
  interactive?: boolean;
  onClick?: () => void;
  tabIndex?: number;
  role?: string;
}

/**
 * TracQ Brand Card-Komponente (Tailwind v4)
 * - Variants: default, elevated, outlined, ghost
 * - Padding: sm, md, lg
 * - Features: interactive states, header/footer slots
 * - TracQ Brand Colors & Typography
 * - Barrierefrei & responsive
 */
export const Card: FC<CardProps> = ({
  children,
  className,
  header,
  footer,
  variant = "default",
  padding = "md",
  interactive = false,
  onClick,
  tabIndex = 0,
  role = "region",
}) => {
  const isClickable = onClick || interactive;

  const variantStyles = {
    default: "bg-gray-900 border border-gray-700 shadow-tracq-card",
    elevated: "bg-gray-800 shadow-xl border-0",
    outlined: "bg-transparent border border-gray-600 shadow-none",
    ghost: "bg-tracq-deep-blue/10 border-0 shadow-none",
  };

  const paddingStyles = {
    sm: "p-3",
    md: "p-5",
    lg: "p-6",
  };

  const interactiveStyles = isClickable
    ? "cursor-pointer transition-all duration-200 ease-in-out hover:shadow-xl hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-tracq-bright-blue focus:ring-offset-2"
    : "";

  return (
    <div
      className={clsx(
        // TracQ Base Styles
        "rounded-tracq font-poppins",
        "text-gray-900", // Default text color

        // Variant Styles
        variantStyles[variant],

        // Padding Styles
        paddingStyles[padding],

        // Interactive Styles
        interactiveStyles,

        className,
      )}
      onClick={onClick}
      tabIndex={isClickable ? tabIndex : undefined}
      role={role}
      aria-label={isClickable ? "Interactive card" : undefined}
    >
      {/* TracQ Header */}
      {header && (
        <div className="mb-4 flex items-center justify-between">
          <div className="text-headline text-tracq-deep-blue font-bold">{header}</div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 text-body text-gray-700">{children}</div>

      {/* TracQ Footer */}
      {footer && (
        <div className="mt-4 pt-3 border-t border-gray-700">
          <div className="text-body-sm text-gray-400">{footer}</div>
        </div>
      )}
    </div>
  );
};
